import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import { TID } from "@/constants/testIds";
import { Plus, Trash } from "@phosphor-icons/react";

export default function BranchesPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", address: "", phone: "" });
  const [err, setErr] = useState("");

  const load = async () => setBranches((await api.get("/branches")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/branches", form);
      setOpen(false); setForm({ name: "", code: "", address: "", phone: "" });
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this branch?")) return;
    await api.delete(`/branches/${id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Branches"
        subtitle="Physical branch offices of the cooperative."
        action={user.role === "super_admin" && (
          <Button onClick={() => setOpen(true)} data-testid={TID.addBtn("branch")}>
            <Plus size={16} weight="bold" /> Add branch
          </Button>
        )}
      />
      <Card>
        {branches.length === 0 ? (
          <EmptyState title="No branches" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Address</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {branches.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`branch-row-${b.id}`}>
                    <td className="px-4 py-3"><Badge tone="info">{b.code}</Badge></td>
                    <td className="px-4 py-3 font-medium">{b.name}</td>
                    <td className="px-4 py-3 text-slate-600">{b.address || "—"}</td>
                    <td className="px-4 py-3 mono text-slate-600">{b.phone || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {user.role === "super_admin" && (
                        <button onClick={() => del(b.id)} data-testid={`del-branch-${b.id}`} className="text-rose-600 hover:text-rose-800">
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Add branch">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Branch name" required testId="branch-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          <Input label="Branch code" required testId="branch-code" value={form.code} onChange={e=>setForm({...form,code:e.target.value})} />
          <Input label="Address" testId="branch-address" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} />
          <Input label="Phone" testId="branch-phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} />
          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid={TID.saveBtn}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
